import React, {
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import { StatusPill, type StatusPillTone } from '../StatusPill';
import { PayerReasonDrawer, type PayerReasonKind } from './PayerReasonDrawer';
import { usePayerResponse } from './usePayerResponse';

/**
 * The status badge, carrying the payer's reasons on a hover popover.
 *
 * The reasons used to sit in the row itself, as a two-line clamped summary above
 * a "Why?" link. That spent two lines of every row on text the biller reads
 * once, and still truncated it. The badge now owns them: the row is one line,
 * and a hover shows every reason in full.
 *
 * The drawer keeps what the popover deliberately leaves out — the last-checked
 * time, the appeal deadline and the Send Again control — and the popover links
 * to it, so the drawer did not lose its entry point when the row lost the "Why?"
 * link.
 *
 * A badge with no reasons renders as a plain `StatusPill` — an empty panel is worse
 * than no panel, and a cursor that invites a hover which shows nothing is a lie.
 * A refusal with no reasons is the one exception: "nothing arrived yet" is an
 * answer the biller needs, and the drawer behind it holds the last-checked time
 * that says how current that is.
 *
 * The hover-on-controlled-`Popover` mechanics follow `ShelfHeading` in
 * `pages/ReportsLanding.tsx`: the design system has no Radix HoverCard, and
 * Popover (not Tooltip) keeps the panel reachable on touch. Hover-capable
 * devices open on mouseenter with a 150ms close grace and treat click as
 * open-only; coarse pointers keep Radix's native tap toggle untouched.
 */
export const PayerReasonBadge: React.FC<{
  kind: PayerReasonKind;
  id: string;
  label: string;
  tone?: StatusPillTone;
  /** Passed through to the `StatusPill`. */
  className?: string;
  reasonCount?: number;
  /** The record's own status, which the drawer needs. */
  status?: string;
  /** True when this record is a refusal, which earns a panel with no reasons. */
  isRefusal?: boolean;
}> = ({
  kind,
  id,
  label,
  tone = 'neutral',
  className,
  reasonCount = 0,
  status,
  isRefusal = false,
}) => {
  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Evaluated once per mount, not on every render — a device's hover/pointer
  // capability doesn't change mid-session.
  const [hoverCapable] = useState(
    () => window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latches on first open so closing the popover does not discard the reasons
  // and refetch them on the next hover. Failed requests receive a new key when
  // the popover reopens, so a transient failure does not strand the row.
  const [hasOpened, setHasOpened] = useState(false);
  const [retryRequest, setRetryRequest] = useState(0);

  const { summary, isLoading, errorMessage } = usePayerResponse(
    kind,
    id,
    hasOpened,
    retryRequest
  );

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    clearCloseTimeout();
    if (errorMessage) setRetryRequest(request => request + 1);
    setHasOpened(true);
    setOpen(true);
  }, [clearCloseTimeout, errorMessage]);

  const closeSoon = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, 150);
  }, [clearCloseTimeout]);

  // Mirrors Radix-initiated changes (outside click, Escape) into our state and
  // clears any pending close timer so it can't later fire against a popover
  // Radix already closed.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        if (errorMessage) setRetryRequest(request => request + 1);
        setHasOpened(true);
      } else clearCloseTimeout();
    },
    [clearCloseTimeout, errorMessage]
  );

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  if (reasonCount === 0 && !isRefusal)
    return (
      <StatusPill tone={tone} className={className}>
        {label}
      </StatusPill>
    );

  const hoverHandlers = hoverCapable
    ? { onMouseEnter: openNow, onMouseLeave: closeSoon }
    : {};

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label={
            reasonCount === 0
              ? `${label}. No payer reason received yet.`
              : `${label}. ${reasonCount} payer ${
                  reasonCount === 1 ? 'reason' : 'reasons'
                }.`
          }
          className='cursor-help rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          onFocus={(e: FocusEvent<HTMLButtonElement>) => {
            if (e.currentTarget.matches(':focus-visible')) openNow();
          }}
          onBlur={closeSoon}
          onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            clearCloseTimeout();
            setOpen(false);
            setDrawerOpen(true);
          }}
          {...(hoverCapable
            ? {
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                  // Suppress Radix's native click-toggle
                  // (composeEventHandlers checks defaultPrevented) so click is
                  // open-only here — hover already owns opening and closing.
                  e.preventDefault();
                  openNow();
                },
              }
            : {})}
          {...hoverHandlers}
        >
          <StatusPill tone={tone} className={className}>
            {label}
          </StatusPill>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side='bottom'
        align='start'
        className='w-80 text-xs'
        onOpenAutoFocus={e => e.preventDefault()}
        {...hoverHandlers}
      >
        {isLoading ? (
          <div className='flex items-center gap-2 text-muted-foreground'>
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
            Loading
          </div>
        ) : null}

        {!isLoading && errorMessage ? (
          <div className='flex flex-col gap-2'>
            <p className='text-red-800'>{errorMessage}</p>
            <Button
              type='button'
              variant='link'
              size='sm'
              className='h-auto justify-start p-0 text-xs'
              onClick={() => {
                setOpen(false);
                setDrawerOpen(true);
              }}
            >
              Full payer response
            </Button>
          </div>
        ) : null}

        {!isLoading && summary ? (
          <div className='flex min-w-0 flex-col gap-2'>
            {summary.reasons.length === 0 ? (
              // Reachable: the row's count came from the list response, and the
              // payer's answer can change between that read and this one.
              <p className='text-muted-foreground'>No reason received yet.</p>
            ) : (
              <ul className='flex min-w-0 flex-col gap-1.5'>
                {summary.reasons.map((reason, index) => (
                  <li
                    key={`${reason.code ?? 'reason'}-${index}`}
                    className='min-w-0'
                  >
                    {reason.code ? (
                      <span className='font-medium'>{reason.code}</span>
                    ) : null}
                    {reason.code && reason.description ? ' — ' : null}
                    {reason.description ?? null}
                    {!reason.code && !reason.description
                      ? 'Reason details unavailable.'
                      : null}
                  </li>
                ))}
              </ul>
            )}

            {summary.appeal_deadline ? (
              <p className='text-foreground'>
                Appeal by {formatDateForDisplay(summary.appeal_deadline)}
              </p>
            ) : null}

            {/* The row no longer carries a "Why?" link, so the drawer's entry
                point lives here. */}
            <Button
              type='button'
              variant='link'
              size='sm'
              className='h-auto justify-start p-0 text-xs'
              onClick={() => {
                setOpen(false);
                setDrawerOpen(true);
              }}
            >
              Full payer response
            </Button>
          </div>
        ) : null}
      </PopoverContent>

      <PayerReasonDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        kind={kind}
        id={id}
        status={status ?? ''}
      />
    </Popover>
  );
};

export default PayerReasonBadge;
